import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { APP_VERSION, wasAppUpdated } from '@/config/version';
import { isStandalonePWA } from '@/lib/isStandalonePWA';

interface PWAUpdateContextType {
  needsRefresh: boolean;
  offlineReady: boolean;
  isUpdating: boolean;
  wasJustUpdated: boolean;
  updateServiceWorker: () => Promise<void>;
  dismissUpdate: () => void;
  dismissUpdateNotice: () => void;
  appVersion: string;
}

const PWAUpdateContext = createContext<PWAUpdateContextType | undefined>(undefined);

export const usePWAUpdate = () => {
  const context = useContext(PWAUpdateContext);
  if (!context) {
    throw new Error('usePWAUpdate must be used within a PWAUpdateProvider');
  }
  return context;
};

interface PWAUpdateProviderProps {
  children: React.ReactNode;
}

export const PWAUpdateProvider: React.FC<PWAUpdateProviderProps> = ({ children }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [wasJustUpdated, setWasJustUpdated] = useState(false);
  // Keep a ref to the poll interval so we can clear it on unmount
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track if a controllerchange listener was added so we only add it once
  const controllerChangeAdded = useRef(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      console.log('[PWA] Service Worker registered:', swUrl);
      if (r) {
        // Clear any existing interval before creating a new one (Strict Mode safety)
        if (pollIntervalRef.current !== null) {
          clearInterval(pollIntervalRef.current);
        }
        // Check for updates every 5 minutes
        pollIntervalRef.current = setInterval(() => {
          r.update();
        }, 5 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('[PWA] Service Worker registration error:', error);
    },
  });

  // Clear the poll interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Add a single controllerchange listener so page reloads after SW swap
  useEffect(() => {
    if (!('serviceWorker' in navigator) || controllerChangeAdded.current) return;
    controllerChangeAdded.current = true;

    const handleControllerChange = () => {
      console.log('[PWA] Controller changed — reloading for new version');
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      controllerChangeAdded.current = false;
    };
  }, []);

  // Send the app version to the SW so it can store it for health checks
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.active) {
        registration.active.postMessage({
          type: 'SET_APP_VERSION',
          version: APP_VERSION,
        });
      }
    }).catch(() => { /* SW not ready yet, ignore */ });
  }, []);

  // "What's new" after an update — installed PWA only
  useEffect(() => {
    if (!isStandalonePWA()) return;
    if (wasAppUpdated()) {
      setWasJustUpdated(true);
    }
  }, []);

  // Browser tabs: apply waiting SW quietly so the site is not stuck on a stale cache.
  // Installed PWA keeps the prompt so the user can update when ready.
  useEffect(() => {
    if (isStandalonePWA()) return;
    if (!needRefresh) return;
    updateServiceWorker(true).catch(() => {});
  }, [needRefresh, updateServiceWorker]);

  // Write version stamp directly from client (belt-and-suspenders for SW race)
  useEffect(() => {
    if (!('caches' in window)) return;
    caches.open('chaoskitchen-version').then((cache) => {
      cache.put('/app-version', new Response(JSON.stringify({
        version: APP_VERSION,
        storedAt: Date.now(),
      })));
    }).catch(() => {});
  }, []);

  const handleUpdate = useCallback(async () => {
    setIsUpdating(true);
    try {
      await updateServiceWorker(true);
      // controllerchange listener above handles the reload
    } catch (error) {
      console.error('[PWA] Failed to update:', error);
      setIsUpdating(false);
    }
  }, [updateServiceWorker]);

  const dismissUpdate = useCallback(() => {
    setDismissed(true);
    // Remind again after 1 hour
    const t = setTimeout(() => {
      setDismissed(false);
    }, 60 * 60 * 1000);
    // Note: this timeout intentionally outlives the component on dismiss
    // because the reminder should fire even after re-mount.
    return () => clearTimeout(t);
  }, []);

  const dismissUpdateNotice = useCallback(() => {
    setWasJustUpdated(false);
  }, []);

  const contextValue: PWAUpdateContextType = {
    needsRefresh: needRefresh && !dismissed,
    offlineReady,
    isUpdating,
    wasJustUpdated,
    updateServiceWorker: handleUpdate,
    dismissUpdate,
    dismissUpdateNotice,
    appVersion: APP_VERSION,
  };

  return (
    <PWAUpdateContext.Provider value={contextValue}>
      {children}
    </PWAUpdateContext.Provider>
  );
};
